import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useSourceListInfinite } from '@/hooks/useSourceListInfinite';
import { toast } from '@/components';
import { EngineService } from '@/services/engine';
import {
    FullSource,
    SourceStatus,
    SourcesSelection,
    SourceVisibility,
} from '@/types/source';
import { SourceFolderChildren } from '@/types/source-folder';
import { FileUploadItem, FileUploadStatus } from '@/types/source-creation';
import { useSourcesStore } from '@/stores';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { SourceType } from '@/types/source';
import type { TypeFilter, SortOption } from './useSourcesFilters';

const PROCESSING_POLL_INTERVAL_MS = 3000;

const PROCESSING_STATUSES = new Set([
    SourceStatus.PROCESSING,
    SourceStatus.PENDING,
    SourceStatus.QUEUED,
    SourceStatus.FIXING,
]);

function hasProcessingSource(sources: FullSource[]): boolean {
    return sources.some((s) => PROCESSING_STATUSES.has(s.status));
}

export function selectionVisibility(
    selection: SourcesSelection,
): SourceVisibility | null {
    switch (selection.kind) {
        case 'private':
            return 'private';
        case 'organization':
            return 'organization';
        case 'folder':
            return null;
    }
}

interface Options {
    selection: SourcesSelection;
    searchQuery: string;
    typeFilter: TypeFilter;
    sortOption: SortOption | null;
    fileItems: FileUploadItem[];
    getFilterParams: () => { type?: SourceType; orderBy?: string };
}

export function useSourceGridData({
    selection,
    searchQuery,
    typeFilter,
    sortOption,
    fileItems,
    getFilterParams,
}: Options) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const { addSources } = useSourcesStore();

    const visibility = selectionVisibility(selection);
    const isFolderScope = selection.kind === 'folder';
    const folderId = selection.kind === 'folder' ? selection.id : null;

    const filterParams = getFilterParams();

    const {
        sources: listSources,
        totalSources: listTotal,
        hasMore: listHasMore,
        isLoading,
        isLoadingMore: listIsLoadingMore,
        error,
        loadMore: loadMoreList,
        resetToFirstPage,
        revalidate: revalidateList,
    } = useSourceListInfinite(
        {
            search: searchQuery || undefined,
            visibility: (visibility ?? 'all') as SourceVisibility,
            type: filterParams.type,
            orderBy: filterParams.orderBy,
        },
        { enabled: !isFolderScope },
    );

    const [hasProcessingInFolder, setHasProcessingInFolder] = useState(false);

    const hasActiveUploadsInSelectedFolder =
        !!folderId &&
        fileItems.some(
            (item) =>
                item.targetFolderId === folderId &&
                (item.status === FileUploadStatus.PENDING ||
                    item.status === FileUploadStatus.UPLOADING),
        );

    const { data: folderChildren, isLoading: folderLoading } =
        useSWR<SourceFolderChildren>(
            folderId
                ? [
                      CACHE_KEYS.SOURCE_FOLDER_CHILDREN,
                      folderId,
                      searchQuery,
                      typeFilter,
                  ]
                : null,
            ([_, currentFolderId, search, type]: [
                string,
                string,
                string,
                TypeFilter,
            ]) =>
                EngineService.getSourceFolderChildren(currentFolderId, {
                    search: search || undefined,
                    sourceType:
                        type !== 'all' ? (type as SourceType) : undefined,
                }),
            {
                revalidateOnFocus: false,
                refreshInterval:
                    folderId &&
                    (hasProcessingInFolder || hasActiveUploadsInSelectedFolder)
                        ? PROCESSING_POLL_INTERVAL_MS
                        : 0,
            },
        );

    useEffect(() => {
        if (!folderId) {
            setHasProcessingInFolder(false);
            return;
        }
        setHasProcessingInFolder(
            hasProcessingSource(folderChildren?.sources ?? []),
        );
    }, [folderId, folderChildren?.sources]);

    const displaySources =
        folderId && folderChildren?.sources
            ? folderChildren.sources
            : listSources;

    useEffect(() => {
        if (isFolderScope) return;
        if (listSources.length > 0) {
            addSources(listSources);
        }
    }, [listSources, addSources, isFolderScope]);

    useEffect(() => {
        if (error) {
            toast.error(t('page.failedToLoad', { type: 'sources' }));
        }
    }, [error, t]);

    const reset = resetToFirstPage;

    const totalSources = isFolderScope
        ? (folderChildren?.sources?.length ?? 0)
        : listTotal;

    const hasMore = !isFolderScope && listHasMore;
    const isLoadingMore = !isFolderScope && listIsLoadingMore;
    const isInitialLoading = isFolderScope
        ? folderLoading && !folderChildren
        : isLoading && displaySources.length === 0;

    return {
        displaySources,
        folderChildren,
        totalSources,
        hasMore,
        isLoadingMore,
        isInitialLoading,
        reset,
        loadMore: loadMoreList,
        revalidateList,
    };
}
